import React, { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { checkBotAccess } from "../../../shared/chatBot/chatBotSlice";
import accountContext from "../../../account/accountContext";
import { Roles } from "../../../account/models/AccountModels";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

/**
 * Route guard for /midas -- redirects to home if bot is not enabled (SEC-REQ-2).
 * Must be wrapped inside AuthorizedRoute for auth check.
 *
 * On browser refresh Redux resets to initial state (botAccessChecked=false,
 * isBotEnabled=false). The guard dispatches checkBotAccess itself and shows
 * a loading spinner until the API responds, preventing a premature redirect.
 *
 * MidasPilot exception (MIDAS_PILOT_ROLE_SPEC.md §5.5): that role is route-locked to
 * /midas, so the bot-disabled redirect to "/" would loop. Pilots get a static dead-end
 * screen instead. See the comment at the !isBotEnabled branch.
 */
const MidasRouteGuard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const isBotEnabled = useAppSelector((state) => state.chatBot.isBotEnabled);
  const botAccessChecked = useAppSelector((state) => state.chatBot.botAccessChecked);
  const isMidasPilot = accountContext.user?.role === Roles.MIDAS_PILOT;

  // If bot access hasn't been checked yet (e.g. after browser refresh),
  // dispatch the check immediately rather than waiting for ChatWidget.
  useEffect(() => {
    if (!botAccessChecked) {
      dispatch(checkBotAccess());
    }
  }, [dispatch, botAccessChecked]);

  // Still waiting for the API response -- show a centered spinner
  if (!botAccessChecked) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Access checked and bot is disabled.
  //
  // MIDAS_PILOT_ROLE_SPEC.md §5.5 — for a MidasPilot the `<Navigate to="/">` below is an
  // INFINITE REDIRECT LOOP, verified chain:
  //   /midas → (this guard, bot disabled) → "/"
  //   "/"    → PilotRouteGuard sees a non-/midas path → "/midas"      [and, failing that,
  //            useCustomerRoutes' `path=""` route → defaultRoute() → "/midas"]
  //   /midas → this guard again → "/" → ...
  // Both of the pilot's own confinement mechanisms point back at /midas, so the pilot must
  // be given a terminal screen rather than a redirect. Every other role still redirects to
  // "/" exactly as before — zero behaviour change off the pilot path.
  if (!isBotEnabled) {
    if (isMidasPilot) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: "100vh",
            width: "100%",
            padding: "24px",
            boxSizing: "border-box",
            backgroundColor: dt.pageBg,
            fontFamily: dt.font,
          }}
        >
          <div
            style={{
              maxWidth: "440px",
              width: "100%",
              textAlign: "center",
              backgroundColor: dt.cardBg,
              border: `1px solid ${dt.cardBorder}`,
              borderRadius: dt.radius,
              boxShadow: dt.cardShadow,
              padding: "32px 28px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "17px",
                fontWeight: 600,
                color: dt.textPrimary,
                marginBottom: "10px",
              }}
            >
              {translate("pilotUnavailableTitle")}
            </div>
            <div style={{ fontSize: "14px", lineHeight: 1.6, color: dt.textSecondary }}>
              {translate("pilotUnavailableMessage")}
            </div>
          </div>
        </div>
      );
    }

    return <Navigate to="/" replace />;
  }

  // Access checked and bot is enabled -- render the MIDAS page
  return <Outlet />;
};

export default MidasRouteGuard;
